/**
 * DEVELOPMENT（04 現像・暗室）
 *
 * - 開くと 0.9 秒で暗室モードへ（設定で無効化）。離れると元に戻る（main.js）
 * - 上部：N± · フィルム · EI と推奨時間
 * - 「条件」カード（開閉）：現像液、温度 ±0.1°C（長押しで連続、シーンに保存）、
 *   個人補正 ±15 秒（フィルム × 現像液ごとに記憶）、内訳
 * - 道具：テストストリップ計算機、現像レシピ、処理工程チェックリスト（進捗 3/8）
 * - タイマー：リング＋現像タンク（液面）＋つまみ、攪拌ブロック（攪拌の型に合わせる）
 *   実行中は条件が畳まれダイヤルが大きくなる。Wake Lock で画面点灯
 *   最初の 30 秒は連続攪拌、60 秒から 30 秒ごとに振動・440Hz（設定で音を消せる）・波紋。残り 10 秒は強調
 *   完了で自動停止、振動 3 回、880Hz（常に鳴らす）、トースト
 * - 「完了・記録」で即保存してシーンノートへ（既存の記録は上書き前に確認）
 */

import {
  showScreen, escapeHtml, toast, setDarkroomMode, isDarkroomMode, isAutoDarkroom, icon, buzz,
  renderRail, holdRepeat, openSheet, confirmDialog, onDarkroomChange, chapterLink,
} from './ui.js';
import { getCurrentShoot, updateCurrentShoot, flushSave } from './scene.js';
import { getDeveloperById } from './data/developers.js';
import { getNTimeSeconds, applyNPlanToTime, formatSeconds } from './data/dev-times.js';
import { getFilmById } from './data/films.js';
import { prefs } from './prefs.js';
import { planLabel } from './zone-calc.js';
import { devOptions } from './gear.js';
import { unlockAudio, agitationBeep, completeBeep, testSound, canIgnoreSilentSwitch } from './signal.js';
import { checklistProgress, runningLabel, onProcessChange } from './checklist.js';

const PERSONAL_OFFSET_KEY = 'zn.dev.personalOffset'; // { "filmId|devId": 秒 }
const CHECK_KEY = 'zn.dev.checklist';

export function getPersonalOffset(filmId, devId) {
  try { return JSON.parse(localStorage.getItem(PERSONAL_OFFSET_KEY) || '{}')[`${filmId}|${devId}`] ?? 0; } catch { return 0; }
}
function setPersonalOffset(filmId, devId, sec) {
  let all = {};
  try { all = JSON.parse(localStorage.getItem(PERSONAL_OFFSET_KEY) || '{}'); } catch {}
  all[`${filmId}|${devId}`] = sec;
  localStorage.setItem(PERSONAL_OFFSET_KEY, JSON.stringify(all));
}
/** 現像画面の「処理工程」ボタンの添え字：計測中の工程と残り時間、なければフィルムの進み具合 */
function procLabel() {
  const running = runningLabel();
  if (running) return running;
  const p = checklistProgress('film');
  return `${p.done}/${p.total}`;
}
// シートを閉じていても、工程タイマーの残り時間をボタンに出す
onProcessChange(() => {
  const c = document.getElementById('dev-chk');
  if (c) c.textContent = procLabel();
});

export function checklistCount() {
  try {
    const c = JSON.parse(localStorage.getItem(CHECK_KEY) || '{}');
    return Object.values(c).filter(Boolean).length;
  } catch { return 0; }
}

/**
 * 温度補正（Massive Dev Chart 準拠）: factor = 2^((20 - T) / 8.5)、0.4〜2.5
 */
export function tempFactor(tempC) {
  const t = Number(tempC);
  if (!Number.isFinite(t)) return 1.0;
  return Math.max(0.4, Math.min(2.5, Math.pow(2, (20 - t) / 8.5)));
}

/** 推奨時間 = N 時間 × N± 倍率 × 温度係数 + 個人補正 */
export function computeRecommendedTime(filmId, devId, plan, tempC, offset, nOverride = null) {
  const nSec = nOverride ?? getNTimeSeconds(filmId, devId);
  if (nSec == null) return { totalSec: 0, nSec: null, breakdown: '標準時間のデータがありません。Massive Dev Chart で調べてください。' };
  const planAdjusted = applyNPlanToTime(nSec, plan);
  const final = Math.max(0, Math.round(planAdjusted * tempFactor(tempC)) + (offset || 0));
  const parts = [`N=${formatSeconds(nSec)}`];
  if (plan !== 'N') parts.push(`${planLabel(plan)}=${formatSeconds(planAdjusted)}`);
  parts.push(`@${Number(tempC).toFixed(1)}°C`);
  parts.push(`${offset >= 0 ? '+' : ''}${offset || 0}s`);
  return { totalSec: final, nSec, breakdown: parts.join(' · ') };
}

/**
 * 13. 攪拌の型（Shoot.devAgitation。レシピの agitation と同じ値）
 *   standard / aggressive：最初の 30 秒は連続、60 秒から 30 秒ごと（旧版と同じ）
 *   gentle：最初の 30 秒は連続、60 秒ごと
 *   stand：最初の 30 秒は連続、全体の半分で 1 回だけ。あとは静置
 */
export const AGITATION = {
  standard: { label: '30 秒ごと', blocks: 30 },
  aggressive: { label: '30 秒ごと（強め）', blocks: 30 },
  gentle: { label: '60 秒ごと', blocks: 60 },
  stand: { label: 'スタンド', blocks: null },
};
function agitationType(shoot) {
  return AGITATION[shoot?.devAgitation] ? shoot.devAgitation : 'standard';
}
/** 合図を鳴らす時刻（秒）の一覧 */
export function agitationTimes(type, target) {
  if (!target) return [];
  if (type === 'stand') return target > 60 ? [Math.round(target / 2)] : [];
  const step = type === 'gentle' ? 60 : 30;
  const out = [];
  for (let t = 60; t < target; t += step) out.push(t);
  return out;
}
/** 攪拌ブロック（進捗の帯）の区切り [開始, 終了] */
function agitationBlocks(type, target) {
  if (!target) return [];
  if (type === 'stand') {
    const half = Math.round(target / 2);
    return target > 60 ? [[0, 30], [30, half], [half, target]] : [[0, target]];
  }
  const size = AGITATION[type].blocks;
  const out = [];
  for (let s0 = 0; s0 < target; s0 += size) out.push([s0, Math.min(target, s0 + size)]);
  return out;
}

// --------------------------------------------------
// 状態
// --------------------------------------------------
const timer = { shootId: null, running: false, startedAt: 0, pausedAt: 0, target: 0, iv: null, wake: null, agN: 0, done: false };

/**
 * 14. タイマーの状態を端末に残し、再読み込み・アプリの開き直しでも続きから計る。
 * 時刻は Date.now()（端末の時計）で持つ。performance.now() は再読み込みで 0 に戻るため。
 */
const TIMER_KEY = 'zn.dev.timer';
function saveTimerState() {
  try {
    if (!timer.shootId || (!timer.running && !timer.pausedAt && !timer.done)) {
      localStorage.removeItem(TIMER_KEY);
      return;
    }
    localStorage.setItem(TIMER_KEY, JSON.stringify({
      shootId: timer.shootId, running: timer.running, startedAt: timer.startedAt,
      pausedAt: timer.pausedAt, agN: timer.agN, done: timer.done, target: timer.target,
    }));
  } catch {}
}
/** ホームの帯などから読む：{ shootId, running, done, elapsedSec, target } | null */
export function storedTimer() {
  try {
    const t = JSON.parse(localStorage.getItem(TIMER_KEY) || 'null');
    if (!t || !t.shootId) return null;
    const el = t.running ? t.pausedAt + (Date.now() - t.startedAt) / 1000 : t.pausedAt;
    return { ...t, elapsedSec: t.target ? Math.min(el, t.target) : el, done: t.done || (t.target > 0 && el >= t.target) };
  } catch { return null; }
}
function restoreTimer(shootId) {
  let t = null;
  try { t = JSON.parse(localStorage.getItem(TIMER_KEY) || 'null'); } catch {}
  if (!t || t.shootId !== shootId) return false;
  Object.assign(timer, { shootId, running: !!t.running, startedAt: t.startedAt || 0, pausedAt: t.pausedAt || 0, done: !!t.done });
  const el = elapsed();
  // 離れていた間の合図はまとめて鳴らさない（render 後に時刻表から数え直す）
  timer.agN = -1;
  if (timer.running) {
    clearInterval(timer.iv);
    timer.iv = setInterval(tick, 100);
    acquireWake().then(paintTimer);
  }
  return true;
}
let condOpen = true;
let unDark = null;

function cur() {
  const shoot = getCurrentShoot();
  const devId = shoot.defaultDeveloperId;
  const plan = shoot.developmentPlan || 'N';
  const tempC = Number.isFinite(shoot.devTempC) ? shoot.devTempC : 20.0;
  const offset = getPersonalOffset(shoot.filmId, devId);
  const rec = computeRecommendedTime(shoot.filmId, devId, plan, tempC, offset, shoot.devNSec ?? null);
  return { shoot, devId, plan, tempC, offset, rec };
}

export function openDevelopment(opts = {}) {
  const shoot = getCurrentShoot();
  if (!shoot) { toast('シーンがありません'); showScreen('home', { back: true }); return; }
  // 同じシーンのタイマーが動いている（一時停止中を含む）ときは続きから
  const keep = timer.shootId === shoot.id && (timer.running || timer.pausedAt > 0);
  if (!keep) {
    // 端末に残した状態は消さずに、メモリ上のタイマーだけ初期化してから読み戻す
    clearInterval(timer.iv);
    releaseWake();
    Object.assign(timer, { running: false, startedAt: 0, pausedAt: 0, agN: 0, done: false });
    timer.shootId = shoot.id;
    condOpen = true;
    if (restoreTimer(shoot.id) && (timer.running || timer.pausedAt > 0)) condOpen = false;
  }
  render();
  showScreen('development', { back: !!opts.back });
  if (prefs.autoDarkroom && !isDarkroomMode()) {
    setTimeout(() => setDarkroomMode(true, { auto: true }), 50);
  }
  unDark?.();
  unDark = onDarkroomChange(() => paintDarkBtn());
}

function render() {
  const el = document.getElementById('screen-development');
  const { shoot, devId, plan, tempC, offset, rec } = cur();
  const film = getFilmById(shoot.filmId);
  const dev = getDeveloperById(devId);
  timer.target = rec.totalSec;

  el.innerHTML = `
    <div class="safe-top"></div>
    <div class="bar" style="padding:0 12px 0 8px">
      <button class="bar__back" type="button" data-to-exp>${icon('back', 22)}露出</button>
      <div class="bar__title">現像</div>
      <button class="bar__boxbtn" type="button" id="dev-dark" style="box-shadow:inset 0 0 0 1px var(--color-ink)"></button>
    </div>
    <div id="dev-rail"></div>
    <div class="dev-head">
      <span style="font:300 32px/1 var(--font-en)">${escapeHtml(planLabel(plan))}</span>
      <span class="en sub" style="font-size:13px;line-height:1.3;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escapeHtml(`${film?.name || shoot.filmId} · EI ${shoot.ei}`)}</span>
      <span class="sub" style="margin-left:auto;font:400 11px/1 var(--font-jp-sans)">推奨</span>
      <span class="num" style="font:400 24px/1 var(--font-en)" id="dev-target">${formatSeconds(rec.totalSec)}</span>
    </div>
    <div class="cond ${condOpen ? 'is-open' : ''}" id="dev-cond">
      <button class="cond__head" type="button" data-cond>
        <span class="sub" style="font:500 12px/1 var(--font-jp-sans)">条件</span>
        <span class="en" style="flex:1;min-width:0;font:500 13px/1 var(--font-en);white-space:nowrap;overflow:hidden;text-overflow:ellipsis" id="dev-summary"></span>
        ${icon('down', 16)}
      </button>
      <div class="cond__body"><div>
        <button class="cond__row" type="button" data-dev-pick style="width:100%;text-align:left">
          <span class="cond__k">現像液</span><span class="en" style="flex:1;font:500 15px/1.2 var(--font-en)">${escapeHtml(dev ? `${dev.brand} ${dev.name} ${dev.dilution}` : devId)}</span><span class="sub" style="font:500 12px/1 var(--font-jp-sans);padding-right:8px">変更</span>
        </button>
        <div class="cond__row">
          <span class="cond__k">温度</span>
          <button class="cond__pm" type="button" data-temp="-1" aria-label="温度を下げる">${icon('minus', 16)}</button>
          <label class="cond__v" style="display:flex;align-items:baseline;justify-content:center"><input class="num" id="dev-temp-in" type="number" inputmode="decimal" min="10" max="30" step="0.1" value="${tempC.toFixed(1)}" aria-label="現像液の温度" style="width:4.2em;text-align:right;font:inherit" /><small>°C</small></label>
          <button class="cond__pm" type="button" data-temp="1" aria-label="温度を上げる">${icon('plus', 16)}</button>
        </div>
        <div class="cond__row">
          <span class="cond__k">個人補正<br><span class="en sub" style="font-size:10px">${escapeHtml(`${film?.name || ''} × ${dev?.name || ''}`)}</span></span>
          <button class="cond__pm" type="button" data-off="-15">−15</button>
          <span class="cond__v" id="dev-off">${offset >= 0 ? '+' : ''}${offset}<small>s</small></span>
          <button class="cond__pm" type="button" data-off="15">+15</button>
        </div>
        <div class="cond__row" style="gap:8px">
          <span class="cond__k">攪拌</span>
          <div class="seg seg--md" style="flex:1">${[['standard', '30 秒ごと'], ['gentle', '60 秒ごと'], ['stand', 'スタンド']].map(([v, t]) => `<button type="button" class="seg__opt ${(agitationType(shoot) === v || (v === 'standard' && agitationType(shoot) === 'aggressive')) ? 'is-on' : ''}" data-agi="${v}">${t}</button>`).join('')}</div>
        </div>
        <div class="en sub" style="padding:0 14px 4px;font-size:11px;line-height:1.4" id="dev-brk">${escapeHtml(rec.breakdown)}</div>
        <div style="padding:0 12px 6px;display:flex;gap:4px 14px;flex-wrap:wrap">${chapterLink('development', '06 現像')}${chapterLink('darkroom-setup', '11 暗室の整え方')}</div>
      </div></div>
    </div>
    <div class="tools3">
      <button class="btn-rule" type="button" data-tool="ts">テストストリップ</button>
      <button class="btn-rule" type="button" data-tool="rec">レシピ</button>
      <button class="btn-rule" type="button" data-tool="chk">処理工程<span class="en sub" style="font-size:11px" id="dev-chk">${procLabel()}</span></button>
    </div>
    <div class="timer-wrap">
      <div class="dial-t" id="dev-dial">
        <div class="dial-t__tank"><div class="dial-t__liq" id="dev-liq" style="height:0%"><div class="dial-t__wave"></div></div></div>
        <svg viewBox="0 0 260 260" width="100%" height="100%" style="position:absolute;inset:0;overflow:visible" aria-hidden="true">
          <circle cx="130" cy="130" r="104" fill="none" stroke="var(--color-grain-alt)" stroke-width="14"></circle>
          <circle id="dev-arc" cx="130" cy="130" r="104" fill="none" stroke="var(--color-ink)" stroke-width="14" stroke-dasharray="653.5" stroke-dashoffset="653.5" transform="rotate(-90 130 130)" style="transition:stroke-dashoffset .12s linear,stroke .3s"></circle>
          <g id="dev-ticks"></g>
          <circle id="dev-knob" cx="130" cy="26" r="10" fill="var(--color-paper)" stroke="var(--color-ink)" stroke-width="2"></circle>
        </svg>
        <div class="dial-t__num"><span class="dial-t__el" id="dev-el">0:00</span><span class="en sub" style="font-size:13px;margin-top:8px">/ <span id="dev-tg2">${formatSeconds(rec.totalSec)}</span></span></div>
      </div>
      <div class="blocks" id="dev-blocks"></div>
      <div class="blocks-lbl"><span>0:00</span><span style="font-family:var(--font-jp-sans)" id="dev-blbl"></span><span id="dev-tg3">${formatSeconds(rec.totalSec)}</span></div>
    </div>
    <div class="dev-status"><span id="dev-st1"></span><span class="sub" id="dev-st2" style="text-align:right"></span></div>
    <div style="display:flex;justify-content:flex-end;padding:0 var(--gutter) 8px;flex:none" id="dev-sound">
      <button type="button" data-sound class="sub" style="font:500 12px/1 var(--font-jp-sans);text-decoration:underline;text-underline-offset:3px;padding:6px 0">合図の音を試す</button>
    </div>
    <div class="dev-ctrl">
      <button class="dev-ctrl__side" type="button" data-reset>${icon('reset')}リセット</button>
      <button class="dev-ctrl__main" type="button" data-main id="dev-main"></button>
      <button class="dev-ctrl__done" type="button" data-complete id="dev-done">${icon('check')}完了・記録</button>
    </div>
  `;

  const rail = { 1: () => import('./mapping.js').then((m) => m.openMapping({ back: true })), 2: () => import('./exposure.js').then((m) => m.openExposure({ back: true })) };
  renderRail(el.querySelector('#dev-rail'), 3, rail);
  el.querySelector('[data-to-exp]').addEventListener('click', rail[2]);
  el.querySelector('#dev-dark').addEventListener('click', () => {
    setDarkroomMode(!isDarkroomMode(), { persist: true });
  });
  el.querySelector('[data-cond]').addEventListener('click', () => {
    condOpen = !condOpen;
    paintCond();
  });
  el.querySelector('[data-dev-pick]').addEventListener('click', openDeveloperPicker);
  el.querySelectorAll('[data-temp]').forEach((b) => holdRepeat(b, () => {
    const t = cur().tempC;
    const next = Math.round(Math.max(10, Math.min(30, t + Number(b.dataset.temp) * 0.1)) * 10) / 10;
    updateCurrentShoot({ devTempC: next });
    refreshNumbers();
  }));
  el.querySelector('#dev-temp-in').addEventListener('change', (e) => {
    const v = parseFloat(e.target.value);
    if (Number.isFinite(v)) updateCurrentShoot({ devTempC: Math.round(Math.max(10, Math.min(30, v)) * 10) / 10 });
    refreshNumbers();
  });
  el.querySelectorAll('[data-agi]').forEach((b) => b.addEventListener('click', () => {
    if (timer.running) { toast('タイマーを止めてから変えてください'); return; }
    updateCurrentShoot({ devAgitation: b.dataset.agi });
    timer.agN = -1; // 鳴らし終えた回数を新しい時刻表で数え直す
    buzz(6);
    el.querySelectorAll('[data-agi]').forEach((x) => x.classList.toggle('is-on', x === b));
    refreshNumbers();
  }));
  el.querySelectorAll('[data-off]').forEach((b) => b.addEventListener('click', () => {
    const c = cur();
    setPersonalOffset(c.shoot.filmId, c.devId, c.offset + Number(b.dataset.off));
    buzz(6);
    refreshNumbers();
  }));
  el.querySelectorAll('[data-tool]').forEach((b) => b.addEventListener('click', () => {
    const t = b.dataset.tool;
    if (t === 'ts') import('./test-strip.js').then((m) => m.openTestStrip());
    if (t === 'rec') import('./recipes.js').then((m) => m.openRecipes());
    if (t === 'chk') import('./checklist.js').then((m) => m.openProcessChecklist(() => {
      const c = document.getElementById('dev-chk');
      if (c) c.textContent = procLabel();
    }));
  }));
  el.querySelector('[data-reset]').addEventListener('click', () => resetTimer());
  el.querySelector('[data-sound]').addEventListener('click', () => {
    testSound();
    toast(canIgnoreSilentSwitch() ? '攪拌音と完了音を鳴らしました。聞こえなければ音量を上げてください' : '攪拌音と完了音を鳴らしました。聞こえなければ音量と消音スイッチを確かめてください', 3800);
  });
  el.querySelector('[data-main]').addEventListener('click', () => {
    if (timer.done) completeDevelopment();
    else if (timer.running) pauseTimer();
    else startTimer();
  });
  el.querySelector('[data-complete]').addEventListener('click', () => { if (!timer.done) completeDevelopment(); });

  paintDarkBtn();
  paintCond();
  refreshNumbers();
}

function paintDarkBtn() {
  const b = document.getElementById('dev-dark');
  if (!b) return;
  const on = isDarkroomMode();
  b.classList.toggle('is-on', on);
  b.innerHTML = `${icon(on ? 'moon-fill' : 'moon', 15)}${on ? (isAutoDarkroom() ? '暗室 · 自動' : '暗室 ON') : '暗室'}`;
}

function paintCond() {
  const c = document.getElementById('dev-cond');
  if (!c) return;
  c.classList.toggle('is-open', condOpen);
  const dial = document.getElementById('dev-dial');
  const size = condOpen ? 176 : 226;
  dial.style.width = `${size}px`;
  dial.style.height = `${size}px`;
  dial.classList.toggle('is-big', !condOpen);
}

/** 条件を変えたとき：表示と目標時間を更新する */
function refreshNumbers() {
  const { shoot, devId, tempC, offset, rec } = cur();
  const dev = getDeveloperById(devId);
  timer.target = rec.totalSec;
  const set = (id, html) => { const e = document.getElementById(id); if (e) e.innerHTML = html; };
  const tIn = document.getElementById('dev-temp-in');
  if (tIn && document.activeElement !== tIn) tIn.value = tempC.toFixed(1);
  set('dev-off', `${offset >= 0 ? '+' : ''}${offset}<small>s</small>`);
  set('dev-brk', escapeHtml(rec.breakdown));
  set('dev-target', formatSeconds(rec.totalSec));
  set('dev-tg2', formatSeconds(rec.totalSec));
  set('dev-tg3', formatSeconds(rec.totalSec));
  set('dev-summary', escapeHtml(`${dev ? `${dev.name} ${dev.dilution}` : devId} · ${tempC.toFixed(1)}°C · ${offset >= 0 ? '+' : ''}${offset}s`));
  // 攪拌ブロックと目盛り
  const agType = agitationType(shoot);
  const blocksDef = agitationBlocks(agType, rec.totalSec);
  timer.blocks = blocksDef;
  timer.agTimes = agitationTimes(agType, rec.totalSec);
  const nb = Math.max(1, blocksDef.length);
  const blocks = document.getElementById('dev-blocks');
  if (blocks) {
    blocks.style.gridTemplateColumns = `repeat(${Math.min(nb, 15)}, 1fr)`;
    blocks.innerHTML = Array.from({ length: nb }, () => '<span><i style="width:0%"></i></span>').join('');
  }
  set('dev-blbl', agType === 'stand' ? 'スタンド：最初の 30 秒と半分で攪拌' : `攪拌ブロック ${AGITATION[agType].blocks} 秒 × ${nb}`);
  const ticks = document.getElementById('dev-ticks');
  if (ticks) {
    const r = 104;
    let out = '';
    for (const x of timer.agTimes) {
      const a = (x / rec.totalSec) * 2 * Math.PI - Math.PI / 2;
      out += `<line data-at="${x}" x1="${(130 + (r - 14) * Math.cos(a)).toFixed(1)}" y1="${(130 + (r - 14) * Math.sin(a)).toFixed(1)}" x2="${(130 + (r + 14) * Math.cos(a)).toFixed(1)}" y2="${(130 + (r + 14) * Math.sin(a)).toFixed(1)}" stroke="var(--color-paper)" stroke-width="2" opacity=".3"></line>`;
    }
    ticks.innerHTML = out;
  }
  paintTimer();
}

function openDeveloperPicker() {
  const { shoot, devId } = cur();
  const body = document.createElement('div');
  body.innerHTML = `
    <div style="padding:0 16px">${devOptions(shoot.filmId, devId)}</div>
    <div style="padding:16px"><button class="btn-line btn-line--block" type="button" data-recipes>レシピから選ぶ</button></div>`;
  const sh = openSheet({ en: 'developer', title: '現像液', body, full: true });
  body.querySelectorAll('[data-v]').forEach((b) => b.addEventListener('click', () => {
    // 手で現像液を変えたら、レシピの N 時間は使わない
    updateCurrentShoot({ defaultDeveloperId: b.dataset.v, devNSec: null, recipeId: null });
    buzz(6);
    sh.close();
    render();
  }));
  body.querySelector('[data-recipes]').addEventListener('click', async () => {
    await sh.close();
    import('./recipes.js').then((m) => m.openRecipes());
  });
}

/** レシピ適用後などに外から描き直す */
export function refreshDevelopment() {
  if (document.getElementById('screen-development')?.classList.contains('screen--active')) render();
}

// --------------------------------------------------
// タイマー
// --------------------------------------------------
function elapsed() {
  return timer.running ? timer.pausedAt + (Date.now() - timer.startedAt) / 1000 : timer.pausedAt;
}

async function startTimer() {
  if (timer.running || timer.done) return;
  timer.running = true;
  timer.startedAt = Date.now();
  saveTimerState();
  condOpen = false;
  paintCond();
  unlockAudio(); // 開始のタップの中で音を有効にする（iOS）
  await acquireWake();
  clearInterval(timer.iv);
  timer.iv = setInterval(tick, 100);
  buzz(10);
  paintTimer();
}

function tick() {
  const el = elapsed();
  // 攪拌の型で決めた時刻に知らせる（13）。agN は鳴らし終えた回数
  const times = timer.agTimes || [];
  if (timer.agN < times.length && el >= times[timer.agN] && el < timer.target) {
    while (timer.agN < times.length && el >= times[timer.agN]) timer.agN++;
    saveTimerState();
    buzz(40);
    if (prefs.agitateBeep) agitationBeep();
    pulse();
  }
  if (timer.target > 0 && el >= timer.target) {
    timer.pausedAt = timer.target;
    timer.running = false;
    timer.done = true;
    saveTimerState();
    clearInterval(timer.iv);
    releaseWake();
    buzz([200, 100, 200, 100, 200]);
    completeBeep(); // 完了音は攪拌音の設定に関係なく鳴らす（旧版と同じ。zn.dev.agitateBeep は攪拌音だけ）
    toast('現像完了');
  }
  paintTimer();
}

function pauseTimer() {
  if (!timer.running) return;
  timer.pausedAt = elapsed();
  timer.running = false;
  saveTimerState();
  clearInterval(timer.iv);
  releaseWake();
  paintTimer();
}

function resetTimer(silent = false) {
  clearInterval(timer.iv);
  releaseWake();
  Object.assign(timer, { running: false, startedAt: 0, pausedAt: 0, agN: 0, done: false });
  saveTimerState();
  if (!silent) { condOpen = true; paintCond(); paintTimer(); }
}

/**
 * 画面の点灯を保つ（Wake Lock）。取れたかどうかを timer.wakeState に残し、表示に使う。
 *   'on'：取得済み / 'lost'：取得できない・解除された / 'unsupported'：端末が未対応
 * 画面を切り替えると OS が解除するので、戻ってきたときに取り直す。
 */
async function acquireWake() {
  if (!('wakeLock' in navigator)) { timer.wakeState = 'unsupported'; return; }
  try {
    timer.wake = await navigator.wakeLock.request('screen');
    timer.wakeState = 'on';
    timer.wake.addEventListener('release', () => {
      timer.wake = null;
      if (timer.running) { timer.wakeState = 'lost'; paintTimer(); }
    });
  } catch (e) {
    console.warn('[wake] failed:', e);
    timer.wake = null;
    timer.wakeState = 'lost';
  }
}

function releaseWake() {
  try { timer.wake?.release(); } catch {}
  timer.wake = null;
  timer.wakeState = null;
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && timer.running && !timer.wake) {
    acquireWake().then(paintTimer);
  }
});

function pulse() {
  const dial = document.getElementById('dev-dial');
  if (!dial) return;
  const p = document.createElement('span');
  p.className = 'pulse';
  dial.appendChild(p);
  setTimeout(() => p.remove(), 1150);
}

function paintTimer() {
  const tg = timer.target;
  const el = elapsed();
  const prog = tg ? Math.min(1, el / tg) : 0;
  const last10 = !timer.done && el > 0 && tg - el <= 10 && tg > 0;
  const C = 2 * Math.PI * 104;
  const $ = (id) => document.getElementById(id);
  if (!$('dev-el')) return;
  $('dev-el').textContent = formatSeconds(Math.floor(el));
  $('dev-arc').setAttribute('stroke-dashoffset', (C * (1 - prog)).toFixed(1));
  $('dev-arc').setAttribute('stroke', last10 ? 'var(--color-alert)' : 'var(--color-ink)');
  $('dev-arc').setAttribute('stroke-width', last10 ? '18' : '14');
  const ang = prog * 2 * Math.PI - Math.PI / 2;
  $('dev-knob').setAttribute('cx', (130 + 104 * Math.cos(ang)).toFixed(1));
  $('dev-knob').setAttribute('cy', (130 + 104 * Math.sin(ang)).toFixed(1));
  $('dev-liq').style.height = `${prog * 100}%`;
  const dial = $('dev-dial');
  dial.classList.toggle('is-running', timer.running);
  dial.classList.toggle('is-last', last10);
  document.querySelectorAll('#dev-ticks line').forEach((l) => l.setAttribute('opacity', Number(l.dataset.at) <= el ? '1' : '.3'));
  if (timer.agN < 0) timer.agN = (timer.agTimes || []).filter((t) => t <= el).length;
  document.querySelectorAll('#dev-blocks > span').forEach((b, i) => {
    const [s, e] = (timer.blocks || [])[i] || [0, tg];
    const f = Math.max(0, Math.min(1, (el - s) / Math.max(1, e - s)));
    b.firstChild.style.width = `${f * 100}%`;
    b.classList.toggle('is-cur', timer.running && f > 0 && f < 1);
  });
  const st1 = $('dev-st1');
  const st2 = $('dev-st2');
  st1.style.color = last10 ? 'var(--color-alert)' : '';
  st1.style.fontWeight = last10 ? '700' : '';
  if (timer.done) st1.textContent = '現像完了 · 自動で一時停止';
  else if (timer.running) {
    const nextAt = (timer.agTimes || []).find((t) => t > el);
    st1.textContent = last10 ? `残り ${formatSeconds(Math.ceil(tg - el))} · まもなく完了`
      : el < 30 ? `連続攪拌 · 残り ${formatSeconds(Math.ceil(30 - el))}`
        : nextAt != null ? `次の攪拌まで ${formatSeconds(Math.ceil(nextAt - el))}` : '静置 · 攪拌はありません';
  }
  else st1.textContent = el > 0 ? '一時停止中' : (tg ? '開始すると画面を点灯したままにします' : '標準時間がないため、タイマーは経過時間だけを計ります');
  st2.style.color = '';
  if (timer.running) {
    if (timer.wakeState === 'on') st2.textContent = '画面点灯中';
    else {
      st2.textContent = timer.wakeState === 'unsupported' ? '画面の点灯を保てない端末です · 自動ロックに注意' : '画面の点灯を保てていません · 自動ロックに注意';
      st2.style.color = 'var(--color-alert)';
    }
  } else st2.textContent = timer.done ? `経過 ${formatSeconds(Math.floor(el))}` : `攪拌 ${AGITATION[agitationType(getCurrentShoot())].label}`;
  const snd = $('dev-sound');
  if (snd) snd.hidden = timer.running;
  const main = $('dev-main');
  if (timer.done) main.innerHTML = '記録して振り返りへ';
  else if (timer.running) main.innerHTML = `${icon('pause')}一時停止`;
  else main.innerHTML = `${icon('play')}${el > 0 ? '再開' : '開始'}`;
  $('dev-done').classList.toggle('is-dim', timer.done);
}

// --------------------------------------------------
// 完了 → 現像記録を保存してシーンノートへ
// --------------------------------------------------
async function completeDevelopment() {
  const { shoot, devId, tempC, rec } = cur();
  if (shoot.development && (shoot.development.actualDevTime || shoot.development.exposureAccuracy)) {
    const ok = await confirmDialog({
      title: '現像の記録を上書きしますか？',
      body: 'このシーンにはすでに現像の記録があります。今回のタイマーの結果で置き換えます（評価とメモは残します）。',
      ok: '上書きする',
    });
    if (!ok) return;
  }
  const el = Math.round(elapsed());
  pauseTimer();
  const prev = shoot.development || {};
  updateCurrentShoot({
    development: {
      developedAt: Date.now(),
      developer: devId,
      tempC,
      targetDevTime: rec.totalSec,
      actualDevTime: el,
      exposureAccuracy: prev.exposureAccuracy ?? null,
      contrastResult: prev.contrastResult ?? null,
      notes: prev.notes || '',
    },
  });
  try {
    await flushSave();
    toast('現像を記録しました（自動保存）');
  } catch {
    toast('保存に失敗しました');
  }
  resetTimer(true);
  import('./reflection.js').then((m) => m.openReflection());
}
